import { useEffect, useState } from "react";
import { useRouteError } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/shared/brand-mark";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { isStaleBuildError, reloadOntoCurrentBuild } from "@/lib/stale-build";

/**
 * Shown while a lazy route chunk downloads. A skeleton page frame rather than
 * a spinner, per the skill's loading rule.
 */
export function RouteFallback() {
  return (
    <div className="space-y-6" role="status">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <Skeleton className="h-64 w-full rounded-lg" />
      <span className="sr-only">Loading page</span>
    </div>
  );
}

/** Whole-screen wait before there is any page to show: the brand mark, pulsing. */
export function AppStatusScreen({ label }: { label: string }) {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3">
        <BrandMark showWordmark={false} className="animate-pulse" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

/**
 * Last line of defence for a render or loader crash. Without it React Router
 * shows its own stack-trace page, which is exactly the "raw error object" the
 * skill forbids.
 *
 * A page chunk that will not load is nearly always a tab that outlived a
 * deploy, and it is not an error to the admin: reload onto the new build
 * instead of showing one. Only if that reload is refused (lib/stale-build)
 * does it fall through to the error.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const staleBuild = isStaleBuildError(error);
  const [reloadRefused, setReloadRefused] = useState(false);

  useEffect(() => {
    if (staleBuild && !reloadOntoCurrentBuild()) setReloadRefused(true);
  }, [staleBuild]);

  if (staleBuild && !reloadRefused) {
    return <AppStatusScreen label="Loading the latest version…" />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <ErrorState
        title={staleBuild ? "Couldn't load this page" : "Something went wrong"}
        error={error}
        onRetry={() => window.location.reload()}
      />
    </div>
  );
}

export function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-4 text-center">
      <p className="text-sm font-medium">This page doesn&rsquo;t exist</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        The link may be out of date, or the screen may not have been built yet.
      </p>
      <Button asChild variant="outline" size="sm">
        <a href="/dashboard">Go to dashboard</a>
      </Button>
    </div>
  );
}
